import React, { useState, useEffect } from 'react';
import SEO from '../components/SEO';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useTranslation } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import {
  Compass, Building2, Home, Briefcase, Paintbrush,
  Wrench, Zap, Droplets, Hammer, Layers, ThermometerSun,
  Shield, Star, Settings, Package, Truck, Users, Award,
  CheckCircle, ArrowRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map icon name string -> component
const ICON_MAP = {
  Compass, Building2, Home, Briefcase, Paintbrush, Wrench, Zap,
  Droplets, Hammer, Layers, ThermometerSun, Shield, Star, Settings,
  Package, Truck, Users, Award, CheckCircle
};

// Fallback hardcoded services if DB is empty
const DEFAULT_MAIN = [
  { id:'d1', title:'Проектиране', description:'Архитектурно проектиране на жилищни и търговски сгради.', icon:'Compass', image:'', category:'main' },
  { id:'d2', title:'Строителство', description:'Изграждане на нови сгради с модерни технологии.', icon:'Building2', image:'', category:'main' },
  { id:'d3', title:'Жилищно строителство', description:'Жилищни комплекси с висок стандарт на изпълнение.', icon:'Home', image:'', category:'main' },
  { id:'d4', title:'Бизнес имоти', description:'Офис и търговски пространства по индивидуален проект.', icon:'Briefcase', image:'', category:'main' },
];
const DEFAULT_CONSTRUCTION = [
  { id:'c1', title:'Зидария', description:'', icon:'Hammer', category:'construction' },
  { id:'c2', title:'Мазилки', description:'', icon:'Layers', category:'construction' },
  { id:'c3', title:'Бояджийски работи', description:'', icon:'Paintbrush', category:'construction' },
  { id:'c4', title:'Плочки и настилки', description:'', icon:'Layers', category:'construction' },
  { id:'c5', title:'Водопровод', description:'', icon:'Droplets', category:'construction' },
  { id:'c6', title:'Електроинсталации', description:'', icon:'Zap', category:'construction' },
  { id:'c7', title:'Изолации', description:'', icon:'ThermometerSun', category:'construction' },
  { id:'c8', title:'Събаряне', description:'', icon:'Wrench', category:'construction' },
];

export default function ServicesPage() {
  const { t } = useTranslation();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/services`)
      .then(r => setServices(r.data || []))
      .catch(() => setServices([]))
      .finally(() => setLoading(false));
  }, []);

  const mainServices = services.filter(s => s.category === 'main');
  const constructionServices = services.filter(s => s.category === 'construction');

  const showMain = mainServices.length > 0 ? mainServices : DEFAULT_MAIN;
  const showConstruction = constructionServices.length > 0 ? constructionServices : DEFAULT_CONSTRUCTION;

  return (
    <div className="min-h-screen bg-background" data-testid="services-page">
      <SEO title="Услуги" description="Строителни и проектантски услуги от GreenCastle." url="/services" />
      <Header />

      <section className="pt-32 pb-16 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-foreground mb-4">{t('services.title')}</h1>
          <p className="text-lg text-muted-foreground max-w-3xl">{t('services.hero_subtitle')}</p>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl font-bold text-foreground mb-12 text-center">{t('services.main_activities')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {showMain.map((service) => {
              const IconComp = ICON_MAP[service.icon] || Wrench;
              return (
                <div key={service.id} className="flex flex-col md:flex-row gap-6 p-6 bg-white rounded-xl border border-border hover:shadow-lg transition-shadow">
                  {service.image ? (
                    <div className="md:w-1/3 flex-shrink-0">
                      <img src={service.image} alt={service.title} className="w-full h-40 object-cover rounded-lg" />
                    </div>
                  ) : (
                    <div className="md:w-1/3 flex-shrink-0 flex items-center justify-center bg-[#1B5E37]/5 rounded-lg h-40">
                      <IconComp className="w-12 h-12 text-[#1B5E37]/40" />
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-[#1B5E37]/10 rounded-lg flex items-center justify-center">
                        <IconComp className="w-5 h-5 text-[#1B5E37]" />
                      </div>
                      <h3 className="font-heading font-semibold text-xl text-foreground">{service.title}</h3>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">{service.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Construction Services */}
      <section className="py-20 bg-accent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-foreground mb-4">{t('services.construction_repair')}</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{t('services.construction_repair_sub')}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {showConstruction.map((service) => {
              const IconComp = ICON_MAP[service.icon] || Wrench;
              return (
                <div key={service.id} className="p-6 bg-white rounded-xl border border-border hover:-translate-y-1 hover:shadow-lg transition-all">
                  <div className="w-12 h-12 bg-[#1B5E37]/10 rounded-xl flex items-center justify-center mb-4">
                    <IconComp className="w-6 h-6 text-[#1B5E37]" />
                  </div>
                  {service.image && (
                    <img src={service.image} alt={service.title} className="w-full h-28 object-cover rounded-lg mb-3" />
                  )}
                  <h3 className="font-heading font-semibold text-lg text-foreground mb-2">{service.title}</h3>
                  {service.description && <p className="text-muted-foreground text-sm leading-relaxed">{service.description}</p>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#1B5E37]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl font-bold text-white mb-4">{t('services.cta_title')}</h2>
          <p className="text-xl text-white/80 mb-8">{t('services.cta_sub')}</p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-[#1B5E37] hover:bg-white/90">
              {t('services.contact_us')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
